import { LoadingContent } from '../components/feedback';
import { LauncherTab } from './LauncherTab';
import { addonKindsOf, addonLabelOf, blocksOperations } from '@mcpanel/core';
import { GameSettingsTab } from './GameSettingsTab';
import { PortsTab } from './PortsTab';
import { BedrockTab } from './BedrockTab';
import { useState } from 'react';
import { ServerTabs, ServerSections } from '../components/ServerNavigation';
import { resolveServerLocation, serverSections } from './server-navigation';
import { Link, useParams, useSearchParams } from 'react-router';
import { api, ApiError } from '../api/client';
import type { ServerState } from '../api/types';
import { Console } from '../components/Console';
import { JobTable } from '../components/JobTable';
import { ServerOverview } from './ServerOverview';
import { ServerActions } from '../components/ServerActions';
import { Empty, ErrorAlert, StateBadge } from '../components/ui';
import { formatDateTime, softwareLabel } from '../format';
import { usePoll } from '../hooks/usePoll';
import { JobProgress } from '../components/JobProgress';
import { ModsTab } from './ModsTab';
import { DangerTab } from './DangerTab';
import { FilesTab } from './FilesTab';
import { PlayersTab } from './PlayersTab';
import { ResourcesTab } from './ResourcesTab';
import { ScheduleTab } from './ScheduleTab';
import { SnapshotsTab, SoftwareTab } from './ServerManage';
import { PageTransition } from '../components/motion';

export function ServerDetail() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { tab, section } = resolveServerLocation(params);
  const selectLocation = (nextTab: string, nextSection?: string) => {
    const next = new URLSearchParams(params);
    next.set('tab', nextTab);
    next.delete('section');
    next.delete('focus');
    if (nextSection) next.set('section', nextSection);
    setParams(next, { replace: true });
  };
  const [stateOverride, setStateOverride] = useState<ServerState>();
  const server = usePoll(
    async () => {
      const result = await api.server(id);
      setStateOverride(undefined);
      return result;
    },
    5000,
    [id],
  );
  const jobs = usePoll(() => api.jobs({ serverId: id, limit: 1000 }), tab === 'overview' ? 2000 : 15000, [id, tab]);

  if (server.error instanceof ApiError && server.error.status === 404) {
    return (
      <div className="stack">
        <Link to="/" className="small">
          ← ダッシュボード
        </Link>
        <Empty>このサーバーは見つかりません。</Empty>
      </div>
    );
  }

  const runningJob = jobs.data?.find(blocksOperations);
  const data = server.data && (stateOverride ? { ...server.data, state: stateOverride } : server.data);
  const capabilities = addonKindsOf(data?.software.family ?? 'vanilla');
  const activeSection = section === 'mods' && data && !capabilities.mods && !capabilities.plugins ? 'bedrock' : section;

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Link to="/" className="small muted" style={{ textDecoration: 'none' }}>
        ← ダッシュボード
      </Link>
      <ErrorAlert error={server.error} />
      <LoadingContent loading={!data && server.loading} shape="overview">
        {data && (
          <>
            <div className="page-header" style={{ marginBottom: 0 }}>
              <div>
                <div className="row">
                  <h1>{data.name}</h1>
                  <StateBadge state={data.state} unreachable={!!data.unreachable} />
                </div>
                <div className="row small muted server-subtitle">
                  <span>
                    {softwareLabel[data.software.family]} {data.software.mcVersion}
                  </span>
                  <span>{data.node ?? '未配置'}</span>
                </div>
                {runningJob && <JobProgress job={runningJob} />}
              </div>
              <ServerActions
                server={data}
                restoreSnapshot={
                  jobs.data?.find((job) => job.status === 'failed' && job.result?.restoreSnapshot)?.result
                    ?.restoreSnapshot
                }
                onDone={() => {
                  void server.reload();
                  void jobs.reload();
                }}
              />
            </div>
            {data.unreachable && (
              <p className="alert danger" role="status">
                稼働中のはずですが、サーバーから応答がありません
                {data.unreachable.lastSeen ? `（最後の応答: ${formatDateTime(data.unreachable.lastSeen)}）` : ''}
                。VM かノード（{data.node ?? '未配置'}）が止まっている可能性があります。
              </p>
            )}
            <ServerTabs tab={tab} onChange={(next) => selectLocation(next)} />
            <div
              data-search-id={tab === 'overview' || tab === 'console' || tab === 'files' ? tab : undefined}
              data-search-tab={tab}
              id="server-panel"
              role="tabpanel"
              aria-labelledby={`server-tab-${tab}`}
              tabIndex={0}
            >
              <PageTransition key={tab}>
                {tab === 'overview' && (
                  <div className="stack">
                    <ServerOverview server={data} />
                    <section className="card">
                      <div className="card-header">
                        <h2>最近のジョブ</h2>
                        <Link to={`/jobs?serverId=${data.id}`}>すべてのジョブ</Link>
                      </div>
                      <ErrorAlert error={jobs.error} />
                      <LoadingContent loading={!jobs.data && jobs.loading}>
                        <JobTable jobs={(jobs.data ?? []).slice(0, 10)} showServer={false} />
                      </LoadingContent>
                    </section>
                  </div>
                )}
                {tab === 'console' && (
                  <Console serverId={data.id} state={data.state} onStateChange={setStateOverride} />
                )}
                {tab === 'backups' && <SnapshotsTab server={data} />}
                {tab === 'players' && <PlayersTab server={data} />}
                {tab === 'files' && <FilesTab server={data} />}
                {(tab === 'extensions' || tab === 'settings') && (
                  <ServerSections
                    sections={serverSections[tab]
                      .filter((item) => item.id !== 'mods' || capabilities.mods || capabilities.plugins)
                      .map((item) =>
                        item.id === 'mods' ? { ...item, label: addonLabelOf(data.software.family) } : item,
                      )}
                    section={activeSection}
                    onChange={(next) => selectLocation(tab, next)}
                  >
                    {activeSection === 'launcher' && <LauncherTab server={data} />}
                    {activeSection === 'ports' && <PortsTab server={data} />}
                    {activeSection === 'bedrock' && <BedrockTab server={data} />}
                    {activeSection === 'mods' &&
                      (addonKindsOf(data.software.family).mods || addonKindsOf(data.software.family).plugins) && (
                        <ModsTab server={data} />
                      )}
                    {activeSection === 'software' && <SoftwareTab server={data} />}
                    {activeSection === 'game-settings' && <GameSettingsTab server={data} />}
                    {activeSection === 'resources' && <ResourcesTab server={data} />}
                    {activeSection === 'schedule' && <ScheduleTab server={data} />}
                    {activeSection === 'danger' && <DangerTab server={data} />}
                  </ServerSections>
                )}
              </PageTransition>
            </div>
          </>
        )}
      </LoadingContent>
    </div>
  );
}
